/**
 * Error Sanitizer Utility
 * 
 * This utility sanitizes error messages from Firebase authentication
 * to prevent information leakage while still providing useful feedback to users.
 */

/**
 * Map of Firebase error codes to user-friendly messages
 * This prevents leaking sensitive information in error messages
 */
const ERROR_MESSAGES = {
  // Authentication errors
  'auth/user-not-found': 'Invalid email or password',
  'auth/wrong-password': 'Invalid email or password',
  'auth/invalid-email': 'Please enter a valid email address',
  'auth/email-already-in-use': 'An account with this email already exists',
  'auth/weak-password': 'Password is too weak. Please use a stronger password',
  'auth/requires-recent-login': 'Please sign in again to continue',
  'auth/user-disabled': 'This account has been disabled',
  'auth/account-exists-with-different-credential': 'An account already exists with the same email address',
  'auth/operation-not-allowed': 'This operation is not allowed',
  'auth/popup-closed-by-user': 'Authentication canceled. Please try again',
  'auth/cancelled-popup-request': 'Authentication canceled. Please try again',
  'auth/popup-blocked':
    'Your browser blocked the sign-in window. Click Continue with Google again, or allow popups for this site.',
  'auth/unauthorized-domain': 'Authentication failed. Please try again later',
  'auth/timeout': 'The operation has timed out. Please try again',

  // Network errors
  'auth/network-request-failed': 'Network error. Please check your connection and try again',

  // CSRF errors
  'auth/missing-csrf-token': 'Security token missing. Please refresh the page and try again',
  'auth/invalid-csrf-token': 'Security token invalid. Please refresh the page and try again',

  // Rate limiting errors
  'auth/too-many-requests': 'Too many unsuccessful attempts. Please try again later',

  // Generic errors
  'auth/internal-error': 'An internal error occurred. Please try again later',
  'auth/invalid-credential': 'Authentication failed. Please try again',
  'auth/invalid-verification-code': 'Invalid verification code',
  'auth/invalid-verification-id': 'Invalid verification ID',
  'auth/captcha-check-failed': 'CAPTCHA verification failed. Please try again',
};

/** User closed the OAuth popup or cancelled before completing sign-in — not a failure. */
const AUTH_CANCELLATION_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
]);

export function isFirebaseAuthCancellation(error) {
  if (!error) return false;
  if (AUTH_CANCELLATION_CODES.has(error?.code)) return true;
  const msg = String(error.message || '').toLowerCase();
  return (
    msg.includes('popup-closed-by-user') ||
    msg.includes('cancelled-popup-request') ||
    msg.includes('popup closed by user')
  );
}

/**
 * Sanitize Firebase authentication error
 * 
 * @param {Error} error - The error object from Firebase
 * @returns {string} A sanitized, user-friendly error message
 */
export const sanitizeFirebaseAuthError = (error) => {
  // If no error, return empty string
  if (!error) {
    return '';
  }

  // Extract error code from Firebase error
  const errorCode = error.code || '';

  // Log the original error for debugging (but don't expose to user)
  console.error('Authentication error:', error);

  // Return sanitized message if available, or a generic message
  return ERROR_MESSAGES[errorCode] || 'Authentication failed. Please try again later';
};

/**
 * Sanitize any error for display to users
 * 
 * @param {Error|string} error - The error object or message
 * @returns {string} A sanitized, user-friendly error message
 */
export const sanitizeError = (error) => {
  // If no error, return empty string
  if (!error) {
    return '';
  }

  // If it's a Firebase auth error, use specialized sanitizer
  if (error.code && error.code.startsWith('auth/')) {
    return sanitizeFirebaseAuthError(error);
  }

  // Log the original error for debugging (but don't expose to user)
  console.error('Application error:', error);

  // For other errors, return a generic message
  // Don't include technical details that might leak implementation info
  return 'An error occurred. Please try again later';
};

/**
 * Create a sanitized error response object
 * 
 * @param {Error|string} error - The error object or message
 * @returns {Object} An object with a sanitized error message
 */
export const createErrorResponse = (error) => {
  return {
    error: sanitizeError(error)
  };
};

export default sanitizeFirebaseAuthError;
